import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { createState, getState, updateState } from '../../api/orgApi.js';
import { Field, FormShell, SaveBar, todayIso } from './orgKit.js';

export function StateFormPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { stateId } = useParams<{ stateId: string }>();
  const editing = Boolean(stateId);

  const [stateCode, setStateCode] = useState('');
  const [stateName, setStateName] = useState('');
  const [countryCode, setCountryCode] = useState('IN');
  const [effectiveFromDate, setEffectiveFromDate] = useState(todayIso());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!stateId) return;
    getState(token, stateId)
      .then((s) => {
        setStateCode(s.stateCode);
        setStateName(s.stateName);
        setCountryCode(s.countryCode ?? 'IN');
        setEffectiveFromDate(s.effectiveFromDate ?? todayIso());
      })
      .catch((e) => setError((e as Error).message));
  }, [token, stateId]);

  async function submit(): Promise<void> {
    if (!stateName.trim()) { setError(t('org.state.form.nameReq')); return; }
    if (!editing && !stateCode.trim()) { setError(t('org.state.form.codeReq')); return; }
    setError(null);
    setBusy(true);
    try {
      if (editing && stateId) {
        await updateState(token, stateId, {
          stateName: stateName.trim(),
          countryCode: countryCode.trim() || undefined,
        });
      } else {
        await createState(token, {
          stateCode: stateCode.trim(),
          stateName: stateName.trim(),
          countryCode: countryCode.trim() || undefined,
          effectiveFromDate,
        });
      }
      navigate('/org/states');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <FormShell label="state-form" title={editing ? t('org.state.form.editTitle') : t('org.state.form.newTitle')} error={error} onSubmit={submit}>
      <Field label={t('org.field.code')} name="stateCode" value={stateCode} onChange={setStateCode} required placeholder={t('org.state.form.codePh')} />
      <Field label={t('org.field.name')} name="stateName" value={stateName} onChange={setStateName} required />
      <Field label={t('org.field.countryCode')} name="countryCode" value={countryCode} onChange={setCountryCode} placeholder={t('org.state.form.countryPh')} />
      {!editing && (
        <Field label={t('org.field.effectiveFrom')} name="effectiveFromDate" value={effectiveFromDate} onChange={setEffectiveFromDate} type="date" required />
      )}
      <SaveBar busy={busy} saveLabel={editing ? t('org.common.saveChanges') : t('org.state.form.create')} cancelTo="/org/states" />
    </FormShell>
  );
}
